import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * LanguageSync — when an employee logs in, switch the UI language to match
 * their `notification_language` preference (set on their profile by admin).
 *
 * Rules:
 *   • Runs ONCE per login transition (null → user) and only for role='employee'.
 *   • Doesn't override an admin's choice.
 *   • If the user later toggles language manually, this hook never overrides
 *     that within the same session because it tracks the previous user id.
 */
export default function LanguageSync() {
  const { user } = useAuth();
  const { language, setLanguage } = useLanguage();
  const lastUserIdRef = useRef(null);

  useEffect(() => {
    const uid = user?.id || null;
    if (!uid) {
      lastUserIdRef.current = null;
      return;
    }
    if (lastUserIdRef.current === uid) return;
    lastUserIdRef.current = uid;

    if (user.role !== 'employee') return;

    const pref = user.notification_language;
    if (!pref || (pref !== 'ar' && pref !== 'en')) return;
    if (pref !== language) {
      setLanguage(pref);
    }
  }, [user, language, setLanguage]);

  return null;
}
